import { useLanguageStore } from '@/modules/core';

import arTranslations from '../locales/ar.json';
import enTranslations from '../locales/en.json';
import idTranslations from '../locales/id.json';

type TranslationKey = string;
type TranslationValues = Record<string, string | number>;

const translations = {
  en: enTranslations,
  ar: arTranslations,
  id: idTranslations,
};

/**
 * Translation hook that provides a function to get translated strings
 * based on the current language from the language store.
 *
 * @returns Object with:
 *   - t: Function to translate a key (key is the English text itself), filling `{name}` placeholders
 *   - language: Current language code
 *   - isRTL: Whether current language is RTL
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { t, isRTL } = useTranslation();
 *
 *   return (
 *     <div>
 *       <h1>{t('Map')}</h1>
 *       <p>{t('Loading...')}</p>
 *     </div>
 *   );
 * }
 * ```
 */
export function useTranslation() {
  const { language, isRTL } = useLanguageStore();

  const t = (key: TranslationKey, values?: TranslationValues): string => {
    const translation = translations[language];
    const text = translation[key as keyof typeof translation] || key;
    if (!values) return text;
    return text.replace(/\{(\w+)\}/g, (match, name: string) =>
      name in values ? String(values[name]) : match
    );
  };

  return {
    t,
    language,
    isRTL,
  };
}
